import { useMemo, useState } from "react";
import UiTable from "../../../components/table/Table.component";
import {
	TableColumn,
	TableColumnTemplate,
} from "../../../models/components/table/table-column.model";
import { TablePagination } from "../../../models/components/table/table-pagination.model";
import { SentReportRow } from "../../../models/pages/sent-reports-row.model";
import { TableMessages } from "../../../models/components/table/table-message.model";
import {
	FieldMessageSeverity,
	UiFieldMessageProps,
} from "../../../models/components/field-message.model";
import { Formatter } from "../../../services/formatter/formatter.service";
import { useBiometricsSentReport } from "../../../hooks/useBiometricsSentReports";
import { useParams } from "react-router-dom";
import { useBiometricsSentReportsCount } from "../../../hooks/useBiometricsSentReportsCount";

const BiometricsSentReport = () => {
	/* Table */

	// Columns
	const columns: TableColumn[] = [
		{
			field: "timestamp",
			title: "PARTICIPANT.BIOMETRICS.SENT_REPORTS.TABLE.COLUMNS.DATE",
			template: TableColumnTemplate.DATE,
			templateOptions: {
				dateFormat: {
					day: "2-digit",
					month: "2-digit",
					year: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
				},
			},
			sortable: true,
		},
		{
			field: "channel",
			title: "PARTICIPANT.BIOMETRICS.SENT_REPORTS.TABLE.COLUMNS.SOURCE",
			template: TableColumnTemplate.TRANSLATE_PREFIX,
			templateOptions: { translationPrefix: "ENUMS.CHANNEL" },
		},
		{
			field: "from",
			title: "PARTICIPANT.BIOMETRICS.SENT_REPORTS.TABLE.COLUMNS.FROM",
		},
		{
			field: "to",
			sortingField: "firstName,lastName",
			title: "PARTICIPANT.BIOMETRICS.SENT_REPORTS.TABLE.COLUMNS.TO",
		},
		{
			field: "recipientName",
			title: "PARTICIPANT.BIOMETRICS.SENT_REPORTS.TABLE.COLUMNS.RECIPIENT_NAME",
		},
		{
			field: "recipientType",
			title: "PARTICIPANT.BIOMETRICS.SENT_REPORTS.TABLE.COLUMNS.RECIPIENT_TYPE",
			template: TableColumnTemplate.TRANSLATE_PREFIX,
			templateOptions: { translationPrefix: "ENUMS.RECIPIENT_TYPE" },
		},
		{
			field: "status",
			title: "PARTICIPANT.BIOMETRICS.SENT_REPORTS.TABLE.COLUMNS.STATUS",
			template: TableColumnTemplate.TAG,
		},
		{
			field: "actionsTemplate",
			template: TableColumnTemplate.BUTTONS,
			alignment: "right",
		},
	];

	const messages = new TableMessages();
	const [pagination, setPagination] = useState<TablePagination>({
		first: 0,
		rows: 10,
		page: 0,
		sortField: "timestamp",
		sortOrder: -1,
		search: undefined,
		filters: undefined,
	});

	// Get data: reports
	const urlParams = useParams<{ participantId: string }>();
	const {
		data: sentReports,
		isLoading,
		isError,
	} = useBiometricsSentReport(Number(urlParams.participantId), pagination);
	const count = useBiometricsSentReportsCount(Number(urlParams.participantId)).data;

	// Rows
	const rows = useMemo(() => {
		if (!sentReports) return [];
		return sentReports.map((item) => {
			item.actionFn = () => {
				if (!item.content) return;
				Formatter.openFile(
					Formatter.base64ToBlob(item.content, "application/pdf"),
					"application/pdf"
				);
			};
			return new SentReportRow(item);
		});
	}, [sentReports]);

	// Messages
	const message: UiFieldMessageProps = isLoading
		? {
				severity: FieldMessageSeverity.LOADING,
				label: messages.loading,
			}
		: isError
			? {
					severity: FieldMessageSeverity.DANGER,
					label: messages.error,
				}
			: !sentReports || sentReports.length === 0
				? {
						severity: FieldMessageSeverity.INFO,
						label: messages.empty,
					}
				: {
						severity: FieldMessageSeverity.INFO,
						label: "",
					};

	// Pagination
	const onPagination = async (event: TablePagination): Promise<void> => {
		setPagination({
			...pagination,
			page: event.page,
			rows: event.rows,
			sortField: event.sortField,
			sortOrder: event.sortOrder,
			first: event.first,
		});
	};

	return (
		<UiTable
			dataKey="sid"
			title="PARTICIPANT.BIOMETRICS.SENT_REPORTS.TITLE"
			columns={columns}
			value={rows}
			message={message}
			isServerPaginated={true}
			pagination={pagination}
			paginationFn={onPagination}
			totalRecords={count ?? 0}
		/>
	);
};

export default BiometricsSentReport;
